
import React, { useState } from 'react';
import { type EnhancedCrawlResult } from '@/types/crawler';
import { toast } from 'sonner';
import { List, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface SitemapRatingWidgetProps {
  result: EnhancedCrawlResult;
  onSelectPage?: (url: string) => void;
}

const SitemapRatingWidget: React.FC<SitemapRatingWidgetProps> = ({ 
  result,
  onSelectPage 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'sample'>('list');

  // Early exit if no sitemap stats
  if (!result.sitemapStats || !result.sitemapStats.total) {
    return (
      <div className="space-y-6 mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex gap-2 items-center">
            <List className="h-5 w-5" />
            <h2 className="text-lg font-medium">Sitemap Statistics</h2>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 border border-border rounded-lg bg-card">
          <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
          <h3 className="text-xl font-medium mb-2">No Sitemap Found</h3>
          <p className="text-center text-muted-foreground">
            We couldn't find a sitemap for this website. Consider creating one to improve your SEO.
          </p>
        </div>
      </div>
    );
  }

  // Get sitemap statistics
  const sitemapStats = result.sitemapStats;
  const successRate = sitemapStats.successRate || 0;
  const successCount = sitemapStats.successful || 0;
  const failedCount = sitemapStats.failed || 0;
  const totalCount = sitemapStats.total || 0;

  // Get the sitemap URLs from the crawlResult
  const allUrls = sitemapStats.allUrls || [];
  
  // Handler for URL click
  const handleUrlClick = (url: string) => {
    if (onSelectPage) {
      onSelectPage(url);
      
      // Show different messages based on URL status
      const urlItem = allUrls.find(item => item.url === url);
      if (urlItem && urlItem.status === 'failed') {
        toast.info("Selected failed URL for analysis - some features may be limited");
      } else {
        toast.info("Selected URL for analysis");
      }
    }
  };

  // Filter URLs based on search term
  const filteredUrls = allUrls.filter(item => 
    item.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 mt-6 pt-6 border-t border-border">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <List className="h-5 w-5" />
          <h2 className="text-lg font-medium">Sitemap Statistics</h2>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={() => setViewMode('list')}
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 px-3 py-1",
              viewMode === 'list' && "bg-primary text-primary-foreground"
            )}
          >
            URL List <span className="ml-2">↓</span>
          </Button>
          <Button 
            onClick={() => setViewMode('sample')}
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 px-3 py-1",
              viewMode === 'sample' && "bg-primary text-primary-foreground"
            )}
          >
            Sample <span className="ml-2">↓</span>
          </Button>
        </div>
      </div>
      
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium">URLs ready for analysis ({successCount}/{totalCount})</span>
          <span className="text-sm font-semibold text-primary">{successRate.toFixed(1)}%</span>
        </div>
        <Progress 
          value={successRate} 
          className="h-3 bg-muted" 
          indicatorClassName={cn("bg-green-600", successRate > 0 ? "" : "bg-transparent")} 
        />
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="text-green-600 h-6 w-6 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-foreground">Ready</div>
            <div className="text-2xl font-bold text-foreground">{successCount}</div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
          <XCircle className="text-red-600 h-6 w-6 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-foreground">Failed</div>
            <div className="text-2xl font-bold text-foreground">{failedCount}</div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-amber-600 h-6 w-6 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-foreground">Duplicates</div>
            <div className="text-2xl font-bold text-foreground">0</div>
          </div>
        </div>
      </div>
      
      {viewMode === 'list' && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="relative mb-4">
            <Input
              className="pl-3 bg-background border-border"
              placeholder="Search URLs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <ScrollArea className="h-[300px] overflow-y-auto rounded-md border border-border bg-background">
            <ul className="space-y-0 divide-y divide-border">
              {filteredUrls.length > 0 ? (
                filteredUrls.map((item, index) => (
                  <li key={index} 
                      className="py-3 px-4 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => handleUrlClick(item.url)}>
                    <div className="flex justify-between items-center gap-3">
                      <span className="truncate flex-1 text-sm text-foreground">{item.url}</span>
                      <span className={cn("px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap", 
                        item.status === 'success' ? "bg-green-600 text-white" : 
                        item.status === 'failed' ? "bg-red-600 text-white" : 
                        "bg-amber-600 text-white")}>
                        {item.status === 'success' ? 'Ready' : item.status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-sm py-6 px-4 text-center text-muted-foreground">
                  No URLs matching your search criteria
                </li>
              )}
            </ul>
          </ScrollArea>
        </div>
      )}
      
      {viewMode === 'sample' && (
        <div className="mt-4 p-6 bg-card rounded-lg border border-border">
          <p className="text-sm text-center text-muted-foreground">
            Sample content would be displayed here
          </p>
        </div>
      )}
    </div>
  );
};

export default SitemapRatingWidget;
